/* ============================================================
   KLIMOSS · Motion runtime
   No dependencies. Idempotent — safe to re-run on section load.

   Safety model: content is only hidden once JS is alive, and it
   is revealed by THREE independent paths, so a broken
   IntersectionObserver can never leave the page blank:
     1. anything already in the first viewport reveals immediately
     2. IntersectionObserver reveals the rest on scroll
     3. a watchdog turns the whole system off if IO never fires

   Every sub-system is isolated in its own try/catch, so a bad
   selector in one section cannot stop the others from binding.
   ============================================================ */
(function () {
  'use strict';

  if (window.KlimossMotion) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ioFired = false;

  document.documentElement.classList.add('kl-motion-ready');

  /* Watchdog: if IntersectionObserver has produced nothing after a
     beat, drop the hidden states entirely. Covers backgrounded tabs,
     bfcache restores, and browsers where IO misbehaves. */
  window.setTimeout(function () {
    if (ioFired) return;
    document.documentElement.classList.remove('kl-motion-ready');
  }, 2500);

  function reveal(el) { el.classList.add('is-in'); }

  function inFirstViewport(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return r.top < (window.innerHeight || 0) && r.bottom > 0;
  }

  /* ---------- 1. Scroll reveal ------------------------------ */
  var revealObserver = null;

  function ensureRevealObserver() {
    if (revealObserver) return revealObserver;
    if (typeof IntersectionObserver === 'undefined') return null;
    revealObserver = new IntersectionObserver(function (entries) {
      ioFired = true;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });
    return revealObserver;
  }

  function applyStagger(root) {
    root.querySelectorAll('[data-kl-stagger]').forEach(function (group) {
      var sel = (group.getAttribute('data-kl-stagger') || '').trim();
      var kids;
      if (!sel) {
        kids = group.children;
      } else {
        /* A leading combinator is only legal with :scope. */
        if (/^[>+~]/.test(sel)) sel = ':scope ' + sel;
        try {
          kids = group.querySelectorAll(sel);
        } catch (e) {
          kids = group.children;
        }
      }
      Array.prototype.forEach.call(kids, function (kid, i) {
        kid.style.setProperty('--kl-i', i);
      });
    });
  }

  function initReveals(root) {
    var obs = ensureRevealObserver();
    applyStagger(root);

    root.querySelectorAll('[data-kl-reveal], .kl-rule, .kl-split').forEach(function (el) {
      if (el.dataset.klBound === '1') return;
      el.dataset.klBound = '1';

      /* Reduced motion, no IO support, or already on screen:
         show it now rather than waiting for a scroll event. */
      if (REDUCED || !obs || inFirstViewport(el)) {
        reveal(el);
        return;
      }
      obs.observe(el);
    });
  }

  /* ---------- 2. Split text into masked lines --------------- */
  function splitText(root) {
    root.querySelectorAll('[data-kl-split]').forEach(function (el) {
      if (el.dataset.klSplitDone === '1') return;
      el.dataset.klSplitDone = '1';

      var original = (el.textContent || '').trim();
      var words = original.split(/\s+/).filter(Boolean);
      if (!words.length) return;

      el.setAttribute('aria-label', original);
      el.textContent = '';
      el.classList.add('kl-split');

      /* Lay out inline word spans first so we can detect real line breaks. */
      var probes = words.map(function (w) {
        var s = document.createElement('span');
        s.textContent = w;
        s.style.whiteSpace = 'pre';
        el.appendChild(s);
        el.appendChild(document.createTextNode(' '));
        return s;
      });

      var lines = [];
      var lastTop = null;
      probes.forEach(function (s, i) {
        var top = Math.round(s.offsetTop);
        if (lastTop === null || Math.abs(top - lastTop) > 2) {
          lines.push([]);
          lastTop = top;
        }
        lines[lines.length - 1].push(words[i]);
      });

      el.textContent = '';
      lines.forEach(function (lineWords, i) {
        var line = document.createElement('span');
        line.className = 'kl-split__line';
        line.setAttribute('aria-hidden', 'true');
        var inner = document.createElement('span');
        inner.className = 'kl-split__inner';
        inner.style.setProperty('--kl-i', i);
        inner.textContent = lineWords.join(' ');
        line.appendChild(inner);
        el.appendChild(line);
      });
    });
  }

  /* ---------- 3. Parallax ----------------------------------- */
  var parallaxEls = [];
  var parallaxTicking = false;

  function collectParallax(root) {
    if (REDUCED) return;
    root.querySelectorAll('[data-kl-parallax]').forEach(function (el) {
      if (el.dataset.klParallaxBound === '1') return;
      el.dataset.klParallaxBound = '1';
      parallaxEls.push(el);
    });
    if (parallaxEls.length) onScroll();
  }

  function onScroll() {
    if (parallaxTicking || !parallaxEls.length) return;
    parallaxTicking = true;
    requestAnimationFrame(function () {
      var vh = window.innerHeight;
      parallaxEls.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var speed = parseFloat(el.dataset.klParallax) || 0.12;
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        el.style.transform = 'translate3d(0,' + (progress * speed * 100).toFixed(2) + 'px,0)';
      });
      parallaxTicking = false;
    });
  }

  /* ---------- 4. Magnetic buttons --------------------------- */
  function initMagnetic(root) {
    if (REDUCED || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    root.querySelectorAll('[data-kl-magnetic]').forEach(function (el) {
      if (el.dataset.klMagBound === '1') return;
      el.dataset.klMagBound = '1';
      var strength = parseFloat(el.dataset.klMagnetic) || 0.28;

      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        el.classList.add('is-pulled');
        el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-pulled');
        el.style.transform = '';
      });
    });
  }

  /* ---------- 5. Marquee (duplicate track to loop) ---------- */
  function initMarquee(root) {
    root.querySelectorAll('.kl-marquee').forEach(function (el) {
      if (el.dataset.klMarqueeBound === '1') return;
      el.dataset.klMarqueeBound = '1';
      var track = el.querySelector('.kl-marquee__track');
      if (!track) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      el.appendChild(clone);
    });
  }

  /* ---------- 6. Accordions --------------------------------- */
  function initAccordions(root) {
    root.querySelectorAll('[data-kl-accordion]').forEach(function (acc) {
      if (acc.dataset.klAccBound === '1') return;
      acc.dataset.klAccBound = '1';
      var single = acc.getAttribute('data-kl-accordion') === 'single';

      acc.querySelectorAll('.kl-acc__trigger').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('.kl-acc__item');
          if (!item) return;
          var open = item.classList.contains('is-open');
          if (single && !open) {
            acc.querySelectorAll('.kl-acc__item.is-open').forEach(function (other) {
              other.classList.remove('is-open');
              var t = other.querySelector('.kl-acc__trigger');
              if (t) t.setAttribute('aria-expanded', 'false');
            });
          }
          item.classList.toggle('is-open', !open);
          btn.setAttribute('aria-expanded', String(!open));
        });
      });
    });
  }

  /* ---------- 7. Tabs (sliding indicator, RTL-aware) -------- */
  function initTabs(root) {
    root.querySelectorAll('[data-kl-tabs]').forEach(function (wrap) {
      if (wrap.dataset.klTabsBound === '1') return;
      wrap.dataset.klTabsBound = '1';

      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
      var indicator = wrap.querySelector('.kl-tabs__indicator');
      if (!tabs.length) return;

      function moveIndicator(tab) {
        if (!indicator) return;
        var list = tab.parentElement;
        indicator.style.width = tab.offsetWidth + 'px';
        indicator.style.transform =
          'translateX(' + (tab.offsetLeft - list.scrollLeft) + 'px)';
      }

      function select(tab, focus) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.setAttribute('tabindex', on ? '0' : '-1');
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        moveIndicator(tab);
        if (focus) tab.focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(tab, false); });
        tab.addEventListener('keydown', function (e) {
          /* RTL: ArrowLeft advances, ArrowRight goes back. */
          var delta = 0;
          if (e.key === 'ArrowLeft') delta = 1;
          else if (e.key === 'ArrowRight') delta = -1;
          else if (e.key === 'Home') { select(tabs[0], true); e.preventDefault(); return; }
          else if (e.key === 'End') { select(tabs[tabs.length - 1], true); e.preventDefault(); return; }
          else return;
          e.preventDefault();
          select(tabs[(i + delta + tabs.length) % tabs.length], true);
        });
      });

      var initial = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
      select(initial, false);

      /* Re-measure once fonts settle and whenever the width changes. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { moveIndicator(initial); });
      }
      window.addEventListener('resize', function () {
        var current = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0];
        if (current) moveIndicator(current);
      });
    });
  }

  /* ---------- Boot ------------------------------------------ */
  function safely(label, fn, root) {
    try {
      fn(root);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('[Klimoss motion] ' + label + ' failed:', e);
      }
    }
  }

  function init(root) {
    root = root || document;
    safely('splitText', splitText, root);
    safely('reveals', initReveals, root);
    safely('parallax', collectParallax, root);
    safely('magnetic', initMagnetic, root);
    safely('marquee', initMarquee, root);
    safely('accordions', initAccordions, root);
    safely('tabs', initTabs, root);
  }

  window.KlimossMotion = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Re-check on load: images and fonts can push elements into view. */
  window.addEventListener('load', function () { safely('reveals', initReveals, document); });

  /* Restored from bfcache — make sure nothing is left hidden. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) document.documentElement.classList.remove('kl-motion-ready');
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* Theme editor: re-init on section load/select. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
})();
